import React, { FC, ReactNode } from 'react';
import { ImageProps } from 'next/image';
import styled from 'styled-components';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import Image from '@/components/Image';
import { mediaBreakpointUp, paddingY } from '@/styles/layout/utils';

interface LargeAssetAndGridBaseProps {
  heading?: string;
  items: {
    title: string;
    description: ReactNode;
  }[];
}

interface LargeAssetAndGridWithImage extends LargeAssetAndGridBaseProps {
  image: ImageProps;
}

interface LargeAssetAndGridWithVideo extends LargeAssetAndGridBaseProps {
  videoSrc: string;
}

export type LargeAssetAndGridProps =
  | LargeAssetAndGridWithImage
  | LargeAssetAndGridWithVideo
  | LargeAssetAndGridBaseProps;

const LargeAssetAndGrid: FC<LargeAssetAndGridProps> = (props) => {
  return (
    <Wrapper>
      <Heading>{props.heading}</Heading>

      {'image' in props && props.image && (
        <ImageWrapper>
          <Image {...props.image} />
        </ImageWrapper>
      )}

      {'videoSrc' in props && props.videoSrc && (
        <ImageWrapper>
          <video loop autoPlay muted playsInline>
            <source src={props.videoSrc} />
          </video>
        </ImageWrapper>
      )}

      <Grid>
        {props.items.map((item) => (
          <GridItem key={item.title}>
            <h6>{item.title}</h6>
            <p>{item.description}</p>
          </GridItem>
        ))}
      </Grid>
    </Wrapper>
  );
};

export default LargeAssetAndGrid;

// Styled Components

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(30)};

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(40)}
    row-gap: ${remCalc(40)};
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(60)};
  }
`;

const Heading = styled.h2`
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  letter-spacing: -0.01em;
  text-align: center;

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(32, 38)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(49, 58)};
  }
`;

const ImageWrapper = styled.div`
  border-radius: ${remCalc(10)};
  overflow: hidden;

  img,
  video {
    width: 100%;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-auto-rows: auto;
  row-gap: inherit;

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 1fr 1fr;
    column-gap: ${remCalc(80)};
  }

  ${mediaBreakpointUp('desktop')} {
    column-gap: ${remCalc(130)};
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(40)};
    column-gap: ${remCalc(172)};
  }
`;

const GridItem = styled.div`
  ${customTextSize(14, 20)};

  h6 {
    ${customTextSize(16, 20)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    margin-bottom: ${remCalc(10)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(18, 26)};

    h6 {
      ${customTextSize(24, 34)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }
  }
`;
